import { HttpErrorResponse, HttpEvent, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { isBrowser } from '../utils/platform.util';
import { catchError, map, throwError } from 'rxjs';
import { ToasterService } from '../services/ui-services.ts/toaster.service';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { AuthService } from '../auth/auth.service';

export const ErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const toastr = inject(ToasterService);
  const translate = inject(TranslateService);
  const router = inject(Router);
  const authService = inject(AuthService);
  const platformId = inject(PLATFORM_ID);

  return next(req).pipe(
    map((event: HttpEvent<any>) => {
      // Handle "Logical Errors" (Result Pattern: 200 OK but isSuccess: false)
      if (isBrowser(platformId) && event instanceof HttpResponse) {
        const body = event.body;
        if (body && body.isSuccess === false) {
          const message = extractErrorMessage(body, translate);
          toastr.error(message);
        }
      }
      return event;
    }),
    catchError((error) => {
      if (!isBrowser(platformId)) {
        return throwError(() => error);
      }

      if (error instanceof HttpErrorResponse) {
        if (error.status === 401) {
          const errorMessage = error.error?.title || 'انتهت الجلسة الخاصة بك برجاء تسجيل الدخول من جديد';
          authService.logout();
          toastr.error(errorMessage);
          setTimeout(() => router.navigate(['/login'], { replaceUrl: true }), 100);
        } else if (error.status === 403) {
          toastr.error('غير مصرح لك بالدخول الى هذه الصفحة');
        } else {
          // General error handling for 400, 500, etc.
          const message = extractErrorMessage(error.error || error, translate);
          toastr.error(message);
        }
      }

      return throwError(() => error);
    }),
  );
};

/**
 * Shared utility to extract message from the project's standard error structure
 */
function extractErrorMessage(errorBody: any, translate: TranslateService): string {
  if (!errorBody) return translate.instant('Services.Messages.SubmitError');

  // 1. Priority: errorMessages array (Standard Validation Result)
  if (errorBody.errorMessages && Array.isArray(errorBody.errorMessages) && errorBody.errorMessages.length > 0) {
    return errorBody.errorMessages.join('\n');
  }

  // 2. Nested ASP.NET Validation Errors
  if (errorBody.errors) {
    return Object.values(errorBody.errors).flat().join('\n');
  }

  // 3. Simple message or detail fields
  return (
    errorBody.error?.description ||
    errorBody.description ||
    errorBody.message ||
    errorBody.detail ||
    errorBody.title ||
    translate.instant('Services.Messages.SubmitError')
  );
}
